/* eslint-disable @typescript-eslint/no-empty-function */
import { useState, useEffect, useRef, useCallback } from "react";
import { publicClient } from "@toorak/tc-common-fe-sdk";

interface PollConfigType {
  url: string;
  interval?: number;
  method: string;
  body?: any;
  onSuccess: (res: any) => boolean | void | Promise<boolean | void>; // Allow both sync and async returns
  onFailure?: ((error: any) => void) | undefined;
  retryCount?: number;
  pollingWaitTime?: number;
  onExceedingPollingWaitTime: () => void;
  retryOn502?: boolean; // New option to enable 502 retry behavior
}

const defaultConfig: PollConfigType = {
  url: "", // url/api to poll
  interval: 3000, // Interval of polling
  method: "GET", // HTTP Method of the api to call
  onSuccess: (res: any) => true, // Callback function on successful polling. Return true to continue, false to stop
  onFailure: (error: any) => {}, // Callback function on failed polling or api failure
  body: undefined, // The data that need to be sent in a post/put call
  retryCount: 0, // Number of times to retry when an api polling call fails
  pollingWaitTime: 36000000, // max time to poll
  onExceedingPollingWaitTime: () => {},
  retryOn502: false // Whether to retry on 502 Bad Gateway responses
};

export const usePolling = () => {
  const [config, setConfig] = useState<PollConfigType>(defaultConfig);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number | undefined>(
    defaultConfig.retryCount
  );

  const persistedIsPolling: any = useRef();
  const poll: any = useRef();

  persistedIsPolling.current = isPolling;

  useEffect(
    () => () => {
      stopPolling();
    },
    []
  );

  useEffect(() => {
    setRetryCount(config.retryCount);
  }, [config.retryCount]);

  const stopPolling = () => {
    if (poll.current) {
      clearTimeout(poll.current);
      poll.current = null;
    }
    setIsPolling(false);
  };

  const startPolling = (newConfig: PollConfigType) => {
    const updatedConfig = { ...config, ...newConfig };
    console.log('Starting polling with config:', {
      url: updatedConfig.url,
      retryCount: updatedConfig.retryCount,
      retryOn502: updatedConfig.retryOn502,
      interval: updatedConfig.interval
    });
    setIsPolling(true);
    setConfig(updatedConfig);
  };

  useEffect(() => {
    if (!isPolling || config.url === "") return;
    
    // Ensure retryCount is properly set from config when polling starts
    if (config.retryCount !== undefined && retryCount !== config.retryCount) {
      console.log('🔄 Updating retryCount from config:', config.retryCount);
      setRetryCount(config.retryCount);
    }
    
    console.log('🚀 Starting polling with current retryCount:', retryCount, 'config retryCount:', config.retryCount);
    runPolling();
    setPollingWaitTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, config.url, config.retryCount]);

  const setPollingWaitTime = () => {
    setTimeout(() => {
      config.onExceedingPollingWaitTime();
      stopPolling();
    }, config.pollingWaitTime);
  };

  const clientByMethod = () => {
    let client;
    switch (config.method) {
      case "GET":
      case "DELETE":
        client = publicClient.get(config.url);
        break;
      case "POST":
      case "PUT":
      case "PATCH":
        client = publicClient.post(config.url, config.body);
        break;
      default:
        client = publicClient.get(config.url);
    }
    return client;
  };

  const makeRequest = useCallback(() => {
    const client = clientByMethod();
    return client
      .then((res: any) => {
        console.log('Polling response:', { status: res?.status, dataStatus: res?.data?.status });
        
        // Check for 502 Bad Gateway if retryOn502 is enabled
        const is502Error = res?.status === 502 || res?.data?.status === 502;
        
        if (config.retryOn502 && is502Error && retryCount && retryCount > 0) {
          console.log(`502 Bad Gateway detected during polling. ${retryCount} attempts remaining.`);
          // eslint-disable-next-line no-unused-expressions
          config.onFailure && config.onFailure({ status: 502, message: '502 Bad Gateway - Retrying' });
          setRetryCount((prevCount: any) => {
            const newCount = prevCount - 1;
            console.log(`Will retry in 2 seconds. New retry count: ${newCount}`);
            return newCount;
          });
          // Wait 2 seconds before retrying for 502 errors
          setTimeout(() => {
            if (persistedIsPolling.current) {
              console.log('Executing 502 retry...');
              makeRequest();
            }
          }, 2000);
          return;
        }
        
        // Handle both sync and async onSuccess callbacks
        const result = config.onSuccess(res);
        return Promise.resolve(result).then((continuePolling) => {
          console.log('onSuccess result:', continuePolling);
          // eslint-disable-next-line no-unused-expressions
          persistedIsPolling.current && continuePolling !== false
            ? scheduleNextPoll()
            : stopPolling();
        }).catch((onSuccessError) => {
          // Handle errors thrown by onSuccess callback
          console.error('Error in onSuccess callback:', onSuccessError);
          // eslint-disable-next-line no-unused-expressions
          config.onFailure && config.onFailure(onSuccessError);
          stopPolling();
        });
      })
      .catch((error: any) => {
        console.log('Polling error:', { 
          status: error?.response?.status, 
          errorStatus: error?.status,
          message: error?.message,
          retryCount,
          configRetryCount: config.retryCount
        });
        
        // Check if this is a 502 error in the catch block (some HTTP clients reject 4xx/5xx)
        const is502Error = 
          error?.response?.status === 502 || 
          error?.status === 502 ||
          (error?.response?.data && error?.response?.data?.status === 502);
        
        // Use current retryCount OR fallback to config.retryCount if state hasn't updated yet
        const currentRetryCount = retryCount !== undefined ? retryCount : config.retryCount;
        
        if (config.retryOn502 && is502Error && currentRetryCount && currentRetryCount > 0) {
          console.log(`502 Bad Gateway detected in catch block. ${currentRetryCount} attempts remaining.`);
          // eslint-disable-next-line no-unused-expressions
          config.onFailure && config.onFailure(error);
          setRetryCount((prevCount: any) => {
            const newCount = (prevCount !== undefined ? prevCount : config.retryCount) - 1;
            console.log(`Will retry in 2 seconds. New retry count: ${newCount}`);
            return newCount;
          });
          // Wait 2 seconds before retrying for 502 errors
          setTimeout(() => {
            if (persistedIsPolling.current) {
              console.log('Executing 502 retry from catch...');
              makeRequest();
            }
          }, 2000);
          return;
        }
        
        if (currentRetryCount && currentRetryCount > 0) {
          console.log(`General error during polling. ${currentRetryCount} attempts remaining.`);
          // eslint-disable-next-line no-unused-expressions
          config.onFailure && config.onFailure(error);
          setRetryCount((prevCount: any) => {
            const newCount = (prevCount !== undefined ? prevCount : config.retryCount) - 1;
            console.log(`Will retry immediately. New retry count: ${newCount}`);
            return newCount;
          });
          // Retry immediately for general errors
          if (persistedIsPolling.current) {
            console.log('Executing general retry...');
            makeRequest();
          }
        } else {
          console.log('No more retries remaining. Stopping polling.');
          // eslint-disable-next-line no-unused-expressions
          config.onFailure && config.onFailure(error);
          stopPolling();
        }
      });
  }, [config, retryCount]);

  const scheduleNextPoll = useCallback(() => {
    if (!persistedIsPolling.current || config.url === "") return;
    
    console.log(`Scheduling next poll in ${config.interval}ms`);
    const timeoutId = setTimeout(() => {
      if (persistedIsPolling.current) {
        makeRequest();
      }
    }, config.interval);
    poll.current = timeoutId;
  }, [config.interval, config.url, makeRequest]);

  const runPolling = useCallback(() => {
    if (config.url === "") return;
    console.log('Starting polling execution');
    makeRequest();
  }, [config.url, makeRequest]);

  return {
    isPolling,
    startPolling,
    stopPolling
  };
};
